import React, { useState } from 'react';
import { Box, Button, Stack, Typography, Paper, Alert } from '@mui/material';
import { Add, Call } from '@mui/icons-material';
import { useAppSelector } from '../../../../app/store';
import { CallLogGrid } from '../grids';
import { AddCallLogDialog, useAddCallLogDialog, ViewCallLogDialog } from '../dialogs';

const CallLogTab: React.FC = () => {
  const { currentCase } = useAppSelector(state => state.caseManage);
  const callLogs = currentCase?.callLogs || [];
  
  const { open: showAddDialog, openDialog: openAddDialog, closeDialog: closeAddDialog } = useAddCallLogDialog();
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedCallLog, setSelectedCallLog] = useState<any>(null);

  const handleRowClick = (callLog: any) => {
    setSelectedCallLog(callLog);
    setShowViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setShowViewDialog(false);
    setSelectedCallLog(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2.5, 
          mb: 3, 
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 1.5
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Call sx={{ color: '#3b82f6', fontSize: 24 }} />
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1e293b',
                  mb: 0.5
                }}
              >
                Call Log
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: '#64748b' }}
              >
                {callLogs.length === 0 
                  ? 'No calls have been logged for this case yet' 
                  : `${callLogs.length} call${callLogs.length !== 1 ? 's' : ''} recorded`
                }
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAddDialog}
            sx={{
              backgroundColor: '#162346',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: '#1a2a52'
              }
            }}
          >
            Add Call
          </Button>
        </Stack>
      </Paper>

      {/* Call Log Grid Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          border: '1px solid #e2e8f0',
          borderRadius: 1.5,
          overflow: 'hidden'
        }}
      >
        {callLogs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert 
              severity="info" 
              sx={{ 
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                '& .MuiAlert-icon': {
                  color: '#0284c7'
                }
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                No call logs available
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
                Click "Add Call" to record the first phone call for this case
              </Typography>
            </Alert>
          </Box>
        ) : (
          <CallLogGrid onRowClick={handleRowClick} />
        )}
      </Paper>

      {/* Dialogs */}
      <AddCallLogDialog
        open={showAddDialog}
        onClose={closeAddDialog}
      />
      
      <ViewCallLogDialog
        open={showViewDialog}
        onClose={handleCloseViewDialog}
        callLog={selectedCallLog}
      />
    </Box>
  );
};

export default CallLogTab;
